// Testimonials.js - Version modifiée (sans flèches, défilement automatique continu)

import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiUser } from 'react-icons/fi';
import { FaStar } from 'react-icons/fa';
import './Testimonials.css';

// Données des témoignages
const testimonialsData = [
  {
    id: 1,
    name: "Fatoumata",
    country: "Sénégal",
    text: "Grâce à cette plateforme, j'ai trouvé un logement près de mon université et rencontré d'autres étudiants africains.",
    rating: 5
  },
  {
    id: 2,
    name: "Ahmed",
    country: "Maroc",
    text: "Les événements proposés m'ont permis de développer mon réseau professionnel en France.",
    rating: 5
  },
  {
    id: 3,
    name: "Marie",
    country: "France",
    text: "En tant qu'étudiante française, j'ai pu aider des étudiants internationaux à s'intégrer.",
    rating: 4
  },
  {
    id: 4,
    name: "Kwame",
    country: "Ghana",
    text: "Le forum m'a sauvé la vie quand j'avais des problèmes administratifs. La communauté est très solidaire.",
    rating: 5
  },
  {
    id: 5,
    name: "Leïla",
    country: "Tunisie",
    text: "J'ai trouvé un job étudiant en moins d'une semaine grâce aux annonces bien organisées.",
    rating: 4
  },
  {
    id: 6,
    name: "Jean",
    country: "Belgique",
    text: "Super plateforme pour échanger avec des étudiants de différentes cultures.",
    rating: 5
  },
  {
    id: 7,
    name: "Aïssata",
    country: "Mali",
    text: "Le système de parrainage m'a beaucoup aidée pour mes débuts à Paris.",
    rating: 5
  },
  {
    id: 8,
    name: "Mehdi",
    country: "Algérie",
    text: "Les ressources pour les démarches administratives sont complètes et à jour.",
    rating: 4
  },
  {
    id: 9,
    name: "Binta",
    country: "Guinée",
    text: "J'ai pu trouver des meubles gratuits pour mon studio grâce à la section dons.",
    rating: 5
  },
  {
    id: 10,
    name: "Pierre",
    country: "Canada",
    text: "La communauté est très active et bienveillante.",
    rating: 4
  }
];

const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const trackRef = useRef(null);
  const cardRef = useRef(null);
  const intervalRef = useRef(null);
  const [cardsPerView, setCardsPerView] = useState(3);

  // Détecter la taille de l'écran
  useEffect(() => {
    const checkScreen = () => {
      const width = window.innerWidth;
      if (width <= 768) {
        setIsMobile(true);
        setCardsPerView(1);
      } else if (width <= 1200) {
        setIsMobile(false);
        setCardsPerView(2);
      } else {
        setIsMobile(false);
        setCardsPerView(3);
      }
    };
    
    checkScreen();
    window.addEventListener('resize', checkScreen);
    
    return () => {
      window.removeEventListener('resize', checkScreen);
    };
  }, []);

  // Étoiles de notation
  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <FaStar 
        key={i} 
        className={`star ${i < rating ? 'filled' : ''}`} 
        size={isMobile ? 16 : 20}
      />
    ));
  };

  // Défilement automatique CONTINU (ne s'arrête jamais)
  useEffect(() => {
    const startAutoScroll = () => {
      intervalRef.current = setInterval(() => {
        setCurrentIndex(prev => {
          const maxIndex = testimonialsData.length - cardsPerView;
          return prev >= maxIndex ? 0 : prev + 1;
        });
      }, 4000);
    };

    startAutoScroll();
    return () => clearInterval(intervalRef.current);
  }, [cardsPerView]);

  // Animation du carrousel
  useEffect(() => {
    if (trackRef.current && cardRef.current) {
      const cardWidth = cardRef.current.offsetWidth + 30; // + gap
      trackRef.current.style.transition = 'transform 0.8s cubic-bezier(0.4, 0, 0.2, 1)';
      trackRef.current.style.transform = `translateX(-${currentIndex * cardWidth}px)`;
    }
  }, [currentIndex]);

  // Gérer le clic sur les points
  const handleDotClick = (index) => {
    clearInterval(intervalRef.current);
    setCurrentIndex(index);
    // Redémarrer le défilement automatique
    setTimeout(() => {
      intervalRef.current = setInterval(() => {
        setCurrentIndex(prev => {
          const maxIndex = testimonialsData.length - cardsPerView;
          return prev >= maxIndex ? 0 : prev + 1;
        });
      }, 4000);
    }, 500);
  };

  // Données à afficher avec duplication pour l'effet infini
  const displayData = [...testimonialsData];
  const totalSlides = displayData.length;

  return (
    <section className="testimonials-section">
      <div className="container">
        <motion.h2 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="testimonials-section-title"
        >
          Ils nous font confiance
        </motion.h2>
        
        <div className="testimonials-wrapper">
          <div className="testimonials-carousel">
            <div className="testimonials-track" ref={trackRef}>
              {displayData.map((testimonial, index) => (
                <div 
                  className="testimonial-card" 
                  key={`${testimonial.id}-${index}`}
                  ref={index === 0 ? cardRef : null}
                >
                  <div className="testimonial-avatar">
                    <FiUser />
                  </div>
                  <div className="testimonial-rating">
                    {renderStars(testimonial.rating)}
                  </div>
                  <p className="testimonial-text">
                    {testimonial.text}
                  </p>
                  <p className="testimonial-author">
                    <strong>{testimonial.name}</strong>, {testimonial.country}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* Indicateurs de slide (points) */}
        <div className="testimonial-dots">
          {Array.from({ length: Math.ceil(totalSlides / cardsPerView) }).map((_, index) => {
            const dotIndex = index * cardsPerView;
            const isActive = currentIndex >= dotIndex && currentIndex < dotIndex + cardsPerView;
            return (
              <button
                key={index}
                className={`testimonial-dot ${isActive ? 'active' : ''}`}
                onClick={() => handleDotClick(dotIndex)}
                aria-label={`Aller au groupe de témoignages ${index + 1}`}
              />
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;